import { AdminDto, AdminsService, ChangePasswordDto, CreateAdminDto, UpdateAdminDto, UpdateAdminPermissionsDto } from "@generated/admin";
import { ApiError } from "@generated/admin/core/ApiError";
import { OpenAPI } from "@generated/admin/core/OpenAPI";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export type Admin = AdminDto;

export const useAdminFindMyPermissions = () => {
  return useQuery<string[], ApiError>({
    queryKey: ["admin", "me", "permissions"],
    queryFn: () => AdminsService.findMyPermissions(),
    enabled: !!OpenAPI.TOKEN,
  });
};

export const useAdminChangeMyPassword = () => {
  return useMutation({
    mutationFn: (dto: ChangePasswordDto) => AdminsService.changeMyPassword({ requestBody: dto }),
  });
};

export const useAdminFindAllAdmins = () => {
  return useQuery<Admin[], ApiError>({
    queryKey: ["admin", "admins"],
    queryFn: () => AdminsService.findAllAdmins(),
    enabled: !!OpenAPI.TOKEN,
  });
};

export const useAdminFindAdminById = (id: number) => {
  return useQuery<Admin, ApiError>({
    queryKey: ["admin", "admins", id],
    queryFn: () => AdminsService.findAdminById({ id }),
    enabled: !!OpenAPI.TOKEN && !!id,
  });
};

export const useAdminGetAdminPermissions = (id: number) => {
  return useQuery<string[], ApiError>({
    queryKey: ["admin", "admins", id, "permissions"],
    queryFn: () => AdminsService.getAdminPermissions({ id }),
    enabled: !!OpenAPI.TOKEN && !!id,
  });
};

export const useAdminGetAllPermissions = () => {
  return useQuery<string[], ApiError>({
    queryKey: ["admin", "permissions"],
    queryFn: () => AdminsService.getAllPermissions(),
    enabled: !!OpenAPI.TOKEN,
  });
};

export const useAdminGetPermissionsByRole = () => {
  return useQuery<Record<string, string[]>, ApiError>({
    queryKey: ["admin", "permissions", "by-role"],
    queryFn: () => AdminsService.getPermissionsByRole(),
    enabled: !!OpenAPI.TOKEN,
  });
};

export const useAdminUpdatePermissions = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, dto }: { id: number; dto: UpdateAdminPermissionsDto }) =>
      AdminsService.updatePermissions({ id, requestBody: dto }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["admin", "admins", variables.id, "permissions"],
      });
      queryClient.invalidateQueries({
        queryKey: ["admin", "admins"],
      });
    },
  });
};

export const useAdminUpdateAdmin = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, dto }: { id: number; dto: UpdateAdminDto }) =>
      AdminsService.updateAdmin({ id, requestBody: dto }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["admin", "admins", variables.id],
      });
      queryClient.invalidateQueries({
        queryKey: ["admin", "admins"],
      });
    },
  });
};

export const useAdminCreateAdmin = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (dto: CreateAdminDto) => AdminsService.createAdmin({ requestBody: dto }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin", "admins"],
      });
    },
  });
};

export const useAdminDeleteAdmin = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => AdminsService.deleteAdmin({ id }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin", "admins"],
      });
    },
  });
};
